import { type PropsWithChildren } from 'react'

import { WagmiProvider, createConfig, http } from 'wagmi'
import { mainnet } from 'wagmi/chains'
import { coinbaseWallet, injected, walletConnect } from 'wagmi/connectors'

// Import at least one chain for initial config
import { type ExtendedChain } from '@lifi/sdk'
import { useSyncWagmiConfig } from '@lifi/wallet-management'

import { defaultCoinbaseConfig } from '@src/configs/wallet/coinbase'
import { defaultMetaMaskConfig } from '@src/configs/wallet/metaMask'
import { defaultWalletConnectConfig } from '@src/configs/wallet/walletConnect'
import { useChains } from '@src/hooks/useChains'
import { createContextLogger } from '@src/utils/logger'

const logger = createContextLogger('StandardEVMProvider')

// Create a basic Wagmi config with standard connectors only (no Abstract wallet)
// Start with at least one chain to satisfy type requirements
// This will be properly extended with chains from useChains in the provider component
const config = createConfig({
    chains: [mainnet], // Need at least one chain initially, will be replaced via useSyncWagmiConfig
    connectors: [
        walletConnect(defaultWalletConnectConfig),
        coinbaseWallet(), // Use default config
        injected(), // Use default config
    ],
    transports: {
        // Default fallback transport
        // Will be extended with proper RPC URLs via useSyncWagmiConfig
        [mainnet.id]: http(),
    },
})

logger.debug('Standard Wallet provider configuration:', {
    abstractWalletConnectorAvailable: false,
})

// Config used internally by this provider
// No longer exported as we're using viem clients directly elsewhere

/**
 * StandardEVMProvider - EVM provider implementation without Abstract wallet support
 * Used when ABSTRACT_CONNECTOR feature is disabled
 */
export const StandardEVMProvider = ({ children }: PropsWithChildren) => {
    const { chains, isSuccess, isError, error } = useChains()

    if (isError) {
        logger.error('Failed to load chains:', error)
    }

    if (isSuccess) {
        logger.info(
            'Chains loaded successfully:',
            chains?.map(c => ({ id: c.id, name: c.name }))
        )
    }

    // Use all chains from the chains hook without filtering
    useSyncWagmiConfig(config, [], chains as ExtendedChain[])

    return (
        <WagmiProvider config={config} reconnectOnMount={false}>
            {children}
        </WagmiProvider>
    )
}
